/**
 * Adds multi-color (and, where there's a back print, front/back) photo
 * cycling to the Hoodie/Tee/Cap cards on Dawn's stock product-card grid
 * (collection pages, related products, search, etc.) — without touching
 * Dawn's own card markup/CSS at all, so the grid's layout, column
 * count, and card size stay exactly as Dawn renders them. Socks is left
 * alone (no real photos for it yet).
 *
 * How it works: Dawn's own .media container already absolute-fills and
 * object-fit:covers any direct <img> child with an opacity transition
 * baked in (base.css .media > img) — that's the same mechanism Dawn's
 * own show_secondary_image hover-swap uses. This builds one <img> per
 * color/angle combo (front, back, front, back, ... same interleave as
 * clvry-shop-grid.liquid's homepage carousel) and steps through them
 * on a single shared timer, so every matched card on the page advances
 * on the same tick together rather than drifting independently.
 */
(function () {
  // Captured synchronously at parse time (works for a deferred script
  // too — document.currentScript is valid during a deferred script's
  // own execution, just not from an async callback later) so the
  // theme's actual CDN asset base can be derived without needing
  // Liquid's asset_url filter, which isn't available to plain JS.
  var OWN_SCRIPT_SRC = document.currentScript && document.currentScript.src;

  var AUTO_ADVANCE_MS = 2800;
  var prefersReducedMotion = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  // handle -> {garment, colors, hasBackprint}. Matches the real Color
  // option values on each product (verse-customizer.js VC_TEE_PHOTOS /
  // VC_HOODIE_PHOTOS / VC_CAP_PHOTOS) — every color that has a real
  // front theme asset. hasBackprint: false (Cap) means there's no back
  // view to alternate with — the cycle just steps between colors
  // instead of between front/back of the same color.
  var TARGETS = {
    "clvry-verse-hoodie": {
      garment: "hoodie",
      colors: ["Black", "White", "Ash", "Carolina Blue", "Graphite Heather", "Light Pink"],
      hasBackprint: true,
    },
    "clvry-verse-tee": {
      garment: "tee",
      colors: ["Black", "White", "Pink", "Lavender", "Anthracite", "Dark Heather Grey", "Dark Heather Blue"],
      hasBackprint: true,
    },
    "clvry-cap": {
      garment: "cap",
      colors: ["Black", "White"],
      hasBackprint: false,
      // Cap's own front-on shot was dropped from its listing gallery
      // (see scripts/*.mjs history) in favor of the angled "left" view
      // used everywhere else for it now — this card cycle follows suit.
      frontSlot: "left",
    },
    "clvry-polo": {
      garment: "polo",
      colors: ["Black", "Navy", "Sport Grey", "White"],
      hasBackprint: false,
      // Polo only has a flat front shot per color, no angled "left" —
      // frontSlot omitted, falls through to the "front" default below.
    },
    "clvry-sweatshirt": {
      garment: "sweatshirt",
      colors: ["Black", "White", "Charcoal", "Carolina Blue", "Light Pink"],
      hasBackprint: false,
      // Same as Polo — front shot only.
    },
    // Blackout/Whiteout Polo: single fixed image on the card — the angled
    // close-up (same file as the homepage banner card, theme asset
    // blackout-macro-*.png) rather than the product's own featured image,
    // which stays the front view on the product page itself.
    "clvry-polo-blackout": { staticAsset: "blackout-macro-black.png", alt: "CLVRY Polo — Blackout Edition" },
    "clvry-polo-whiteout": { staticAsset: "blackout-macro-white.png", alt: "CLVRY Polo — Whiteout Edition" },
  };

  function handleize(str) {
    return str.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  }

  // Deriving a garment PNG's URL from this script's OWN asset_url means
  // the PNG never gets its own real Shopify version token — the regex
  // below strips whatever query string was on the script tag entirely
  // (no "/" in "file.js?v=123", so it's all one match), leaving a bare
  // ".../assets/sweatshirt-front-black.png" with NO cache-busting. That
  // exact URL is then cacheable forever by the browser and by Shopify's
  // own CDN edge, so swapping the underlying PNG silently keeps serving
  // the old bytes — no amount of hard-refreshing on the client fixes
  // that, since the URL itself never changed. CLVRY_MOCKUP_ASSET_VERSION
  // (set in theme.liquid, bumped on every mockup swap) is appended here
  // so the URL genuinely changes whenever the PNGs do.
  function assetUrl(file) {
    if (!OWN_SCRIPT_SRC) return null;
    var base = OWN_SCRIPT_SRC.replace(/[^/]+$/, file);
    var version = window.CLVRY_MOCKUP_ASSET_VERSION || "1";
    return base + (base.indexOf("?") === -1 ? "?" : "&") + "cb=" + encodeURIComponent(version);
  }

  // Cards use small (900px wide) webp copies of the garment photos, named
  // card-<original name>.webp, instead of the 2000px originals the product
  // page uses — the originals were 300KB+ each, and a collection page loaded
  // 14 of them per card. Regenerate them with sharp if a photo changes.
  function cardFile(name) {
    return "card-" + name.replace(/[.]png$/, "") + ".webp";
  }

  // Frames after the first are loaded a moment after the page has finished
  // (one at a time), so they never compete with what the visitor can see.
  var pending = [];
  function makeImg(file, alt, eager) {
    var img = document.createElement("img");
    img.alt = alt || "";
    img.decoding = "async";
    img.style.opacity = "0";
    var url = assetUrl(cardFile(file));
    if (eager) img.src = url;
    else { img.dataset.cardSrc = url; pending.push(img); }
    return img;
  }
  function isLoaded(img) {
    return img.complete && img.naturalWidth > 0;
  }
  function loadPending() {
    var saveData = navigator.connection && navigator.connection.saveData;
    if (saveData) return;
    var i = 0;
    (function next() {
      if (i >= pending.length) return;
      var img = pending[i++];
      img.src = img.dataset.cardSrc;
      window.setTimeout(next, 120);
    })();
  }

  function staticImg(target) {
    return makeImg(target.staticAsset, target.alt, true);
  }

  // Every matched card's full image sequence + its currently-visible
  // index, so one shared step() can advance them all together —
  // independent per-card timers drift out of sync with each other
  // almost immediately, which is the opposite of what's wanted here:
  // Hoodie and Tee should always be on the same step as each other.
  var cards = [];

  function setUp(cardEl) {
    if (cardEl.dataset.clvryMockupsInit) return;
    var link = cardEl.querySelector('a[href*="/products/"]');
    if (!link) return;
    var handle = link.getAttribute("href").split("/products/")[1].split(/[?#]/)[0];
    var target = TARGETS[handle];
    if (!target) return;

    var media = cardEl.querySelector(".card__media .media");
    if (!media) return;
    if (!OWN_SCRIPT_SRC) return;
    cardEl.dataset.clvryMockupsInit = "true";

    // These garment cutouts have a transparent background (they're
    // built for compositing onto the homepage hero), so simply
    // stacking them on top left Dawn's real product photo showing
    // through the gaps around the silhouette — looked like 2-3
    // overlapping shirts at once. Hiding Dawn's original image (kept
    // in the DOM, not removed, so alt text etc. is unaffected) fixes
    // that; our own images then become the only visible layer.
    var originalImg = media.querySelector("img");
    if (originalImg) originalImg.style.display = "none";
    // Matches the cream background these transparent cutouts are
    // designed to sit on elsewhere (homepage grid, product page),
    // instead of Dawn's default neutral-gray card tint showing through.
    media.style.backgroundColor = "#f3efe5";

    var productName = link.textContent.trim();
    var imgs = target.staticAsset ? (function () { var one = staticImg(target); media.appendChild(one); return [one]; })() : target.colors.map(function (color, colorIndex) {
      var slug = handleize(color);
      // Only the very first frame of a card loads straight away (eager);
      // the rest are queued (see makeImg / loadPending above). Plain
      // loading="lazy" can't be used here: every frame sits stacked in the
      // same visible spot, so the browser never decides to fetch the
      // hidden ones.
      // frontSlot lets a target override which asset-filename slot its
      // "first frame" comes from (Cap uses its angled "left" view, see
      // TARGETS above) — defaults to "front" for everything else,
      // independent of hasBackprint (that flag only controls whether a
      // *second*, back-print frame exists to alternate with).
      var frontSlot = target.frontSlot || "front";
      var frontImg = makeImg(
        target.garment + "-" + frontSlot + "-" + slug,
        productName + " — " + color + (target.hasBackprint ? " (front)" : ""),
        colorIndex === 0
      );
      media.appendChild(frontImg);

      if (!target.hasBackprint) return [frontImg];

      var backImg = makeImg(
        target.garment + "-backprint-" + slug,
        productName + " — " + color + " (back, with verse example)",
        false
      );
      media.appendChild(backImg);

      return [frontImg, backImg];
    }).flat();

    imgs[0].style.opacity = "1";
    cards.push({ imgs: imgs, activeIndex: 0 });
  }

  document.addEventListener("DOMContentLoaded", function () {
    document.querySelectorAll(".card-wrapper").forEach(setUp);
    if (!cards.length) return;

    // Start fetching the remaining frames shortly after the page has fully loaded.
    function startQueue() { window.setTimeout(loadPending, 600); }
    if (document.readyState === "complete") startQueue();
    else window.addEventListener("load", startQueue);

    if (prefersReducedMotion) return;

    window.setInterval(function () {
      cards.forEach(function (card) {
        // Next frame that has actually loaded (skips ones still queued).
        var nextIndex = card.activeIndex;
        for (var tries = 0; tries < card.imgs.length; tries++) {
          nextIndex = (nextIndex + 1) % card.imgs.length;
          if (isLoaded(card.imgs[nextIndex])) break;
        }
        if (nextIndex === card.activeIndex || !isLoaded(card.imgs[nextIndex])) return;
        card.imgs[card.activeIndex].style.opacity = "0";
        card.imgs[nextIndex].style.opacity = "1";
        card.activeIndex = nextIndex;
      });
    }, AUTO_ADVANCE_MS);
  });
})();
